import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Trash2, X } from "lucide-react";
import { useState } from "react";

import {
  autorizarBaja,
  autorizarVacacion,
  getDiasFestivos,
  getVacacionesPendientes,
  rechazarBaja,
  rechazarVacacion,
} from "@/lib/api";
import { diasHabiles, tipoColor, tipoLabel } from "@/lib/tipos";

function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function AutorizacionesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["vacaciones", "pendientes"], queryFn: getVacacionesPendientes });
  const festivosQ = useQuery({ queryKey: ["dias-festivos"], queryFn: getDiasFestivos, staleTime: Infinity });
  const festivos = new Set((festivosQ.data ?? []).map((f) => f.fecha));

  const [aviso, setAviso] = useState<{ texto: string; ok: boolean } | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["vacaciones"] });
  const mAut = useMutation({
    mutationFn: autorizarVacacion,
    onSuccess: (v) => {
      invalidate();
      // El backend manda a RH el formato PDF ("Vacaciones de X AUTORIZADAS").
      setAviso(
        v.formato_rh_enviado
          ? { ok: true, texto: `Vacaciones de ${v.empleado_nombre ?? v.empleado_email} autorizadas. El formato PDF se envió a RH.` }
          : {
              ok: false,
              texto: `Vacaciones de ${v.empleado_nombre ?? v.empleado_email} autorizadas, pero el formato NO se pudo enviar a RH. El colaborador puede reenviarlo con el botón ✈ en Mis vacaciones.`,
            },
      );
    },
  });
  const mRec = useMutation({ mutationFn: rechazarVacacion, onSuccess: invalidate });
  const mAutBaja = useMutation({ mutationFn: autorizarBaja, onSuccess: invalidate });
  const mRecBaja = useMutation({ mutationFn: rechazarBaja, onSuccess: invalidate });
  const working =
    mAut.isPending || mRec.isPending || mAutBaja.isPending || mRecBaja.isPending;

  const rows = q.data ?? [];

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Autorizaciones</h1>
        <p className="text-sm text-slate-500 mt-1">
          Solicitudes de vacaciones de tu equipo pendientes de autorizar (o de dar de baja).
        </p>
      </div>

      {aviso && (
        <div
          className={`border rounded p-3 text-sm ${
            aviso.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          {aviso.texto}
        </div>
      )}
      {mAut.isPending && (
        <div className="text-sm text-slate-500">
          Autorizando y enviando el formato PDF a RH… (tarda unos segundos)
        </div>
      )}

      {q.isLoading && <div className="text-sm text-slate-500">Cargando…</div>}
      {!q.isLoading && rows.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          No tienes solicitudes pendientes. 🎉
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const baja = r.estatus === "baja_pendiente";
          return (
            <div key={r.id} className="bg-white border rounded-lg p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[16rem]">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{r.empleado_nombre}</span>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      baja ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {baja ? "Baja solicitada" : "Pendiente"}
                  </span>
                  {r.fuera_politica && (
                    <span
                      className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                      title="Se capturó después del cierre de la quincena anterior al goce (política MPP)"
                    >
                      Fuera de política
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tipoColor(r.tipo) }} />
                  {tipoLabel(r.tipo)} · {fmtFecha(r.fecha_inicio)} – {fmtFecha(r.fecha_fin)} ·{" "}
                  <b>{diasHabiles(r.fecha_inicio, r.fecha_fin, festivos)}</b> días háb.
                </div>
                {r.comentario && <div className="text-xs text-slate-400 mt-0.5">{r.comentario}</div>}
              </div>
              <div className="flex gap-2">
                {baja ? (
                  <>
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => mAutBaja.mutate(r.id)}
                      className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" /> Autorizar baja
                    </button>
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => mRecBaja.mutate(r.id)}
                      className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <X className="w-4 h-4" /> Mantener
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => mAut.mutate(r.id)}
                      className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" /> Autorizar
                    </button>
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => mRec.mutate(r.id)}
                      className="inline-flex items-center gap-1 border border-red-200 text-red-700 rounded px-3 py-1.5 text-sm hover:bg-red-50 disabled:opacity-50"
                    >
                      <X className="w-4 h-4" /> Rechazar
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
